import { useEffect } from "react";
import { Link } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Pause, Pencil, Play, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import {
  useCreateCriterion,
  useCriteria,
  useDeleteCriterion,
  useToggleCriterion,
} from "@/api/criteria";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { EmptyState } from "@/components/EmptyState";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const CriterionSchema = z.object({
  name: z.string().min(1, "Required"),
  target_titles: z.string().optional(),
  keywords: z.string().optional(),
  locations: z.string().optional(),
  seniority: z.string().optional(),
  description: z.string().optional(),
  min_fit_score: z.coerce.number().int().min(0).max(100),
});
type Form = z.infer<typeof CriterionSchema>;

export function CriteriaPage() {
  useEffect(() => {
    document.title = "Criteria — Beacon";
    return () => { document.title = "Beacon"; };
  }, []);

  const { data, isLoading } = useCriteria();
  const create = useCreateCriterion();
  const toggle = useToggleCriterion();
  const remove = useDeleteCriterion();

  const { register, handleSubmit, reset, formState: { errors } } = useForm<Form>({
    resolver: zodResolver(CriterionSchema),
    defaultValues: { min_fit_score: 60 },
  });

  const onCreate = (values: Form) =>
    create.mutate(values, {
      onSuccess: () => {
        toast.success("Criterion added");
        reset({ min_fit_score: 60 });
      },
      onError: (e) => toast.error(`Couldn't save: ${(e as Error).message}`),
    });

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Search criteria</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Active criteria are matched against every new listing. Add as many profiles as you like.
        </p>
      </div>

      {isLoading ? (
        <div className="h-24 animate-pulse rounded-lg bg-muted/40" />
      ) : !data || data.length === 0 ? (
        <EmptyState
          title="No criteria yet"
          description="Define what you're looking for to start receiving alerts."
        />
      ) : (
        <div className="space-y-3">
          {data.map((c) => (
            <Card key={c.id} className={c.active ? "" : "opacity-65"}>
              <CardHeader className="space-y-1.5 pb-3">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <CardTitle className="text-base">{c.name}</CardTitle>
                  <Badge variant={c.active ? "default" : "muted"}>
                    {c.active ? "Active" : "Paused"}
                  </Badge>
                </div>
                {c.description && (
                  <p className="text-sm text-muted-foreground">{c.description}</p>
                )}
              </CardHeader>
              <CardContent className="space-y-3">
                <dl className="grid gap-x-6 gap-y-1.5 text-sm sm:grid-cols-2">
                  {c.target_titles && (
                    <Meta label="Titles" value={c.target_titles} />
                  )}
                  {c.keywords && <Meta label="Keywords" value={c.keywords} />}
                  {c.locations && <Meta label="Locations" value={c.locations} />}
                  {c.seniority && <Meta label="Seniority" value={c.seniority} />}
                  <Meta label="Min fit" value={`${c.min_fit_score}%`} />
                </dl>

                <div className="flex flex-wrap gap-2 pt-1">
                  <Button asChild variant="outline" size="sm">
                    <Link to="/criteria/$criterionId/edit" params={{ criterionId: String(c.id) }}>
                      <Pencil size={14} /> Edit
                    </Link>
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() =>
                      toggle.mutate(c.id, {
                        onSuccess: (r) => toast.success(r.active ? "Activated" : "Paused"),
                      })
                    }
                  >
                    {c.active ? <Pause size={14} /> : <Play size={14} />}
                    {c.active ? "Pause" : "Activate"}
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => {
                      if (confirm(`Delete "${c.name}"?`)) {
                        remove.mutate(c.id, {
                          onSuccess: () => toast.success("Deleted"),
                        });
                      }
                    }}
                  >
                    <Trash2 size={14} /> Delete
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Plus size={16} /> Add criterion
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onCreate)} className="grid gap-4 sm:grid-cols-2">
            <Field label="Name" required error={errors.name?.message}>
              <Input placeholder="e.g. Senior backend roles" {...register("name")} />
            </Field>
            <Field label="Target titles">
              <Input
                placeholder="comma-separated, e.g. Backend Engineer, Platform Engineer"
                {...register("target_titles")}
              />
            </Field>
            <Field label="Keywords">
              <Input placeholder="e.g. Python, Go, Kubernetes" {...register("keywords")} />
            </Field>
            <Field label="Locations">
              <Input placeholder="e.g. Reykjavík, Remote" {...register("locations")} />
            </Field>
            <Field label="Seniority">
              <Input placeholder="e.g. mid, senior" {...register("seniority")} />
            </Field>
            <Field label="Min fit score">
              <Input type="number" min={0} max={100} step={5} {...register("min_fit_score")} />
            </Field>
            <div className="sm:col-span-2">
              <Field label="What you're looking for">
                <Textarea
                  rows={3}
                  placeholder="Free text describing the ideal role"
                  {...register("description")}
                />
              </Field>
            </div>
            <div className="sm:col-span-2">
              <Button type="submit" disabled={create.isPending}>
                {create.isPending ? "Adding…" : "Add criterion"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

function Meta({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex gap-2">
      <dt className="font-medium text-muted-foreground">{label}</dt>
      <dd className="text-foreground">{value}</dd>
    </div>
  );
}

function Field({
  label,
  required,
  children,
  error,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
  error?: string;
}) {
  return (
    <div className="space-y-1.5">
      <Label>
        {label}
        {required && <span className="text-primary"> *</span>}
      </Label>
      {children}
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}
